import isEmpty from 'lodash/isEmpty';
import React from 'react';
import Select from '../../../../components/Common/Select';
import { getWidgetProducts } from '../../../../services/api';
import { getLocation } from '../../../../utilities/helperUtility';
import Translation from '../../../../utilities/localizationUtility';

export default class ProductSelect extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      bookingFields: [],
      products: [],
    };

    this.onProductChange = this.onProductChange.bind(this);
  }

  componentDidMount() {
    const { product } = this.props;

    if (product.widget_id) {
      getWidgetProducts(product.widget_id)
        .then(({ data: { booking_fields: bookingFields, products } }) => this.setState({ bookingFields, products }))
        .catch(() => this.setState({ products: [] }));
    } else {
      this.setState({ products: [product] });
    }
  }

  onProductChange(id) {
    const { bookingFields, products } = this.state;
    const {
      settings,
      fetchProduct,
      onTrigger,
      setConfiguration,
      setSelectedResource,
      setVariantsQuantity,
    } = this.props;

    try {
      const product = products.find((item) => item.id === id);
      if (!product) {
        throw new Error(`Product with id ${id} not found`);
      }

      const { resources } = product;
      const variant = product.variants?.[0];
      if (!variant) {
        throw new Error(`No variants found for product ${product.title}`);
      }

      fetchProduct(product);

      // Smart resource preservation logic
      const preservedResource = this.preserveResourceIfValid(resources);
      setSelectedResource(preservedResource);

      setVariantsQuantity(`${variant.external_id}_quantity`);
      setConfiguration({ ...settings, product, bookingFields });

      // Smart location preservation logic
      this.preserveLocationIfValid(product);

      if (onTrigger) {
        onTrigger('variantChanged', variant);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error during product change:', error);
      // Graceful fallback - don't update anything if there's an error
    }
  }

  // Generic utility function to validate if a selected item exists in available items
  // eslint-disable-next-line class-methods-use-this
  isSelectionValid(selectedItem, availableItems) {
    return selectedItem && availableItems
           && availableItems.find((item) => item.id === selectedItem.id);
  }

  preserveResourceIfValid(availableResources) {
    const { selectedResource } = this.props;

    if (!selectedResource || !availableResources) {
      return availableResources && availableResources.length === 1 ? availableResources[0] : null;
    }

    // Check if currently selected resource exists in the new product's resources
    const isResourceValid = this.isSelectionValid(selectedResource, availableResources);

    if (isResourceValid) {
      return selectedResource; // Preserve the current selection
    }

    // If current resource not available, use default logic
    return availableResources.length === 1 ? availableResources[0] : null;
  }

  preserveLocationIfValid(product) {
    const { locations } = product;
    const {
      selectedLocation,
      setSelectedLocation,
      setTimeZone,
    } = this.props;

    // Check if current location exists in new product's locations
    if (this.isSelectionValid(selectedLocation, locations)) {
      // Location is valid, keep it and update timezone if needed
      setSelectedLocation(selectedLocation);
      setTimeZone(selectedLocation.timezone || window.bta?.timeZone);
      return;
    }

    // Location not valid or not selected, use default logic
    this.resetLocation(product);
  }

  resetLocation(product) {
    const { locations } = product;
    const {
      settings: { setting },
      setSelectedLocation,
      setTimeZone,
    } = this.props;
    const location = getLocation(locations, setting, { useDefault: true });

    if (isEmpty(location)) {
      setSelectedLocation(null);
      setTimeZone(window.bta?.timeZone);
    } else {
      setSelectedLocation(location);
      setTimeZone(location.timezone || window.bta?.timeZone);
    }
  }

  productOptions() {
    const { products } = this.state;

    return products.map(({ id, title }) => ({
      label: title,
      value: id,
    }));
  }

  render() {
    const {
      bodyStyle,
      className,
      isDisabled,
      labelClass,
      product,
      selectStyles,
      settings,
      wrapperClass,
    } = this.props;
    const t = new Translation(settings);
    const productOptions = this.productOptions();

    return (
      <div className={wrapperClass}>
        <span className={labelClass} style={bodyStyle}>
          {t.key('productPrompt') || 'Select Product'}
        </span>
        <Select
          name="products"
          styles={selectStyles}
          className={className}
          value={product ? product.id : ''}
          options={productOptions}
          isDisabled={isDisabled}
          isClearable={false}
          isSearchable={false}
          onChange={this.onProductChange}
        />
      </div>
    );
  }
}
